import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  UserIcon, 
  CalendarIcon, 
  AcademicCapIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const ProfileCompletion = () => {
  const navigate = useNavigate();
  const { user, loading, profileCompleted, updateProfile } = useAuth();
  
  const [formData, setFormData] = useState({
    displayName: user?.displayName || '',
    dateOfBirth: '',
    academicLevel: 'Class 12',
    preferredSubjects: ['Mathematics', 'Physics', 'Chemistry'],
    targetColleges: ['VJTI Mumbai'],
    currentPerformance: 'Beginner',
    examDate: '2024-04-15'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  console.log('ProfileCompletion: user =', user);
  console.log('ProfileCompletion: loading =', loading);
  console.log('ProfileCompletion: profileCompleted =', profileCompleted);
  console.log('ProfileCompletion: rendering component');

  // Effect to handle navigation after profile completion
  useEffect(() => {
    if (profileCompleted) {
      console.log('ProfileCompletion: Profile completed, navigating to dashboard');
      navigate('/dashboard');
    }
  }, [profileCompleted, navigate]);
  
  // Redirect to login if not authenticated
  if (!loading && !user) {
    console.log('ProfileCompletion: No user, redirecting to login');
    navigate('/login');
    return null;
  }

  // Redirect to profile view if profile is already completed
  if (!loading && profileCompleted) {
    console.log('ProfileCompletion: Profile already completed, redirecting to profile view');
    navigate('/profile');
    return null;
  }
  
  // Note: Profile completion navigation is handled by useEffect above
  
  // Show loading while checking auth state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Note: Navigation is handled by ProtectedRoute in App.js
  // This component only shows when profile is not completed

  const academicLevels = [
    'Class 11',
    'Class 12',
    'Dropper',
    'Repeater'
  ];

  const subjects = [
    'Mathematics',
    'Physics', 
    'Chemistry'
  ];

  const colleges = [
    'VJTI Mumbai',
    'COEP Pune', 
    'SPIT Mumbai',
    'PICT Pune',
    'VIT Pune',
    'DY Patil',
    'Other'
  ];

  const performanceLevels = [
    'Beginner',
    'Intermediate',
    'Advanced'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubjectToggle = (subject) => {
    setFormData(prev => ({
      ...prev,
      preferredSubjects: prev.preferredSubjects.includes(subject)
        ? prev.preferredSubjects.filter(s => s !== subject)
        : [...prev.preferredSubjects, subject]
    }));
  };

  const handleCollegeToggle = (college) => {
    setFormData(prev => ({
      ...prev,
      targetColleges: prev.targetColleges.includes(college)
        ? prev.targetColleges.filter(c => c !== college)
        : [...prev.targetColleges, college]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    console.log('Submitting profile data:', formData);
    
    try {
      await updateProfile(formData);
      // Navigation will be handled by the useEffect above when profileCompleted becomes true
    } catch (error) {
      console.error('Profile update error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 px-4 sm:px-6 lg:px-8 py-12">
      <div className="max-w-2xl mx-auto">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-gray-600 hover:text-blue-600 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Dashboard
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 bg-gradient-to-r from-blue-600 to-orange-500 rounded-full flex items-center justify-center mb-4">
            <UserIcon className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-2">
            Complete Your Profile
          </h2>
          <p className="text-gray-600">
            Help us personalize your CET preparation journey
          </p>
        </div>

        {/* Profile Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="displayName"
                    type="text"
                    value={formData.displayName}
                    onChange={(e) => handleInputChange('displayName', e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your full name"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Birth
                </label>
                <div className="relative">
                  <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="dateOfBirth"
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Academic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="academicLevel" className="block text-sm font-medium text-gray-700 mb-2">
                  Current Academic Level
                </label>
                <div className="relative">
                  <AcademicCapIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <select
                    id="academicLevel"
                    value={formData.academicLevel}
                    onChange={(e) => handleInputChange('academicLevel', e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {academicLevels.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="currentPerformance" className="block text-sm font-medium text-gray-700 mb-2">
                  Current Performance Level
                </label>
                <select
                  id="currentPerformance"
                  value={formData.currentPerformance}
                  onChange={(e) => handleInputChange('currentPerformance', e.target.value)}
                  className="block w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  {performanceLevels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Preferred Subjects */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Preferred Subjects (Select all that apply)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {subjects.map(subject => (
                  <label key={subject} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.preferredSubjects.includes(subject)}
                      onChange={() => handleSubjectToggle(subject)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{subject}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Target Colleges */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Target Colleges (Select your preferred colleges)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {colleges.map(college => (
                  <label key={college} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.targetColleges.includes(college)}
                      onChange={() => handleCollegeToggle(college)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{college}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* CET Exam Date */}
            <div>
              <label htmlFor="examDate" className="block text-sm font-medium text-gray-700 mb-2">
                Expected CET Exam Date
              </label>
              <input
                id="examDate"
                type="date"
                value={formData.examDate}
                onChange={(e) => handleInputChange('examDate', e.target.value)}
                className="block w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Submit Button */}
            <div className="pt-6">
                             <button
                 type="submit"
                 disabled={isSubmitting || !formData.displayName || !formData.dateOfBirth}
                 className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-orange-500 hover:from-blue-700 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
               >
                 {isSubmitting ? 'Updating Profile...' : 'Complete Profile & Start Learning'}
               </button>
            </div>
          </form>
        </div>

        {/* Progress Indicator */}
        <div className="mt-6 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 rounded-full">
            <div className="w-2 h-2 bg-blue-600 rounded-full mr-2"></div>
            <span className="text-sm text-blue-800">Step 1 of 3: Profile Setup</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletion;
